import { Store } from 'stores/type';
import debounce from 'lodash/debounce';
import { logger } from 'utils';

import { Firebase } from './../types';
import FirebaseMailAuth from './mail';

type Listeners = {
  onLogin: () => void,
  onLogout: () => void,
};

class FirebaseAuthWrapper {
  private $store!: Store;
  private firebase!: Firebase;
  private unsubscribe: firebase.Unsubscribe | null = null;
  private listeners!: Listeners;
  public mail!: FirebaseMailAuth;

  constructor() {
    this.onAuthStateChanged = debounce(this.onAuthStateChanged, 250, {
      leading: true,
      trailing: false,
    });
  }

  public async init(firebase: Firebase, store: Store, listeners: Listeners) {
    this.firebase = firebase;
    this.$store = store;
    this.listeners = listeners;

    await this.load();
    this.mail = new FirebaseMailAuth(firebase, this.onAuthStateChanged);

    return this.initAuth();
  }
  private async load() {
    // @ts-ignore
    await import(/* webpackChunkName: "firebase-auth" */ 'firebase/auth');
  }
  private initAuth() {
    if (this.unsubscribe !== null) {
      this.unsubscribe();
    }

    this.unsubscribe = this.auth.onAuthStateChanged(this.onAuthStateChanged as any, (error) => this.log(error));
  }


  public login(type: 'google' | 'facebook') {
    if (type === 'google') {
      return this.loginGoogle();
    }

    if (type === 'facebook') {
      return this.loginFacebook();
    }

    return Promise.reject();
  }
  private loginGoogle() {
    const provider = new this.firebase.auth.GoogleAuthProvider();
    return this.loginWithProvider(provider);
  }
  private loginFacebook() {
    const provider = new this.firebase.auth.FacebookAuthProvider();
    provider.setCustomParameters({
      display: 'popup',
    });

    return this.loginWithProvider(provider);
  }
  private async loginWithProvider(provider: firebase.auth.AuthProvider) {
    try {
      const result = await this.auth.signInWithPopup(provider);
      this.log(provider.providerId, 'login success', result);
      this.onAuthStateChanged(result.user, result.additionalUserInfo);
      return result;
    } catch (error) {
      this.log(provider.providerId, 'login error', error);
      throw new Error(error.message);
    }
  }

  public async logout() {
    try {
      await this.auth.signOut();
      this.onLoggedOut();
    } catch (error) {
      this.log('logout error', error);
      throw new Error(error.message);
    }
  }

  private onAuthStateChanged = (
    data: firebase.User | null,
    additional: firebase.auth.AdditionalUserInfo | null | undefined,
  ) => {
    this.log('onAuthStateChanged', data);

    if (!data) {
      this.onNotLoggedIn();
      return;
    }

    const user = {
      isAnonymous: data.isAnonymous,
      uid: data.uid,
      name: data.displayName,
      email: data.email,
      emailVerified: data.emailVerified,
      avatar: data.photoURL,
      providerId: additional ? additional.providerId : '',
      isNewUser: additional ? additional.isNewUser : false,
    };

    this.onLoggedIn(user);
  }

  private onLoggedIn(user: any) {
    this.$store.dispatch('loggedIn', user);
    this.listeners.onLogin();
  }
  private onNotLoggedIn() {
    this.$store.dispatch('notLoggedIn');
  }
  private onLoggedOut() {
    this.$store.dispatch('loggedOut');
    this.initAuth();
    this.listeners.onLogout();
  }

  get auth() {
    return this.firebase.auth();
  }
  private log(...messages: any[]) {
    logger('firebase', '[AUTH]', ...messages);
  }
}

export default FirebaseAuthWrapper;
